import React, { createContext, useContext, useEffect, useState } from "react";
import { api, resetSessionExpirySignal } from "@/lib/api";

const AuthContext = createContext(null);
const SESSION_EXPIRED_EVENT = "palletnext:session-expired";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const onSessionExpired = () => {
      localStorage.removeItem("pallet_token");
      sessionStorage.removeItem("h3poe_onboarding_complete");
      setUser(false);
      setReady(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setReady(true);
      return;
    }
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch (err) {
        // 401 is expected when not logged in — don't log noise. Log other errors.
        if (err?.response?.status !== 401) console.warn("auth /me failed:", err);
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    if (r.data.token) localStorage.setItem("pallet_token", r.data.token);
    resetSessionExpirySignal();
    // Re-fetch /auth/me so we have the full user document (including created_at).
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch {
      setUser(r.data);
    }
    return r.data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("pallet_token");
    resetSessionExpirySignal();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
